import { useDebounceFn } from '@vueuse/core';
import { onScopeDispose, watch } from 'vue';
import { useSocket } from '@/composition/use-socket';
import { OPTION_GROUPS_CHANGED_EVENT } from '@/domain/catalog/catalog-events';
import { EventBus } from '@/modules/EventBus';
import { useRestaurantStore } from '@/stores/restaurant.store';

/** A burst of `catalogUpdate` events becomes one reload. */
const CATALOG_UPDATE_DEBOUNCE_MS = 300;

/**
 * Calls `reload` after the current restaurant's realtime `catalogUpdate` (a
 * catalog change saved anywhere: another device, the Menú page, the
 * assistant), after the assistant panel changed its option groups (a group
 * no live product uses changes without that event), and after a reconnect,
 * which may have missed one; a burst of them becomes one call. Follows store
 * and socket changes, and stops with the calling component.
 */
export function useCatalogUpdateListener(reload: () => void): void {
  const realtime = useSocket();
  const restaurantStore = useRestaurantStore();
  let disposed = false;
  let detach: () => void = () => undefined;

  const reloadSoon = useDebounceFn(() => {
    if (!disposed) reload();
  }, CATALOG_UPDATE_DEBOUNCE_MS);

  watch(
    [realtime.socket, () => restaurantStore.restaurant?.id],
    ([socket, restaurantId]) => {
      detach();
      detach = () => undefined;
      if (!socket || !restaurantId) return;
      const event = `r:${restaurantId}:catalogUpdate`;
      const onCatalogUpdate = () => {
        void reloadSoon();
      };
      socket.on(event, onCatalogUpdate);
      detach = () => {
        socket.removeListener(event, onCatalogUpdate);
      };
    },
    { immediate: true, flush: 'sync' }
  );

  watch(realtime.isConnected, (connected, wasConnected) => {
    if (connected && wasConnected === false) void reloadSoon();
  });

  /** The panel changed option groups of a store: this one's only. */
  const onOptionGroupsChanged = (restaurantId: unknown) => {
    if (restaurantId === restaurantStore.restaurant?.id) void reloadSoon();
  };
  EventBus.$on(OPTION_GROUPS_CHANGED_EVENT, onOptionGroupsChanged);

  onScopeDispose(() => {
    disposed = true;
    detach();
    EventBus.$off(OPTION_GROUPS_CHANGED_EVENT, onOptionGroupsChanged);
  });
}
